/**
 * Prospects API service — Feature 041
 * All calls route through Admin.Api via the /api proxy (YARP gateway).
 */

import axiosClient from './axiosClient';
import type {
  Prospect,
  ProspectListResponse,
  ProspectFilterOptions,
} from '../types/prospect';
import type { ProspectReply } from '../types';

export interface ListProspectsParams {
  pageNumber?: number;
  pageSize?: number;
  sortBy?: string | null;
  sortDir?: 'asc' | 'desc' | null;
  ownerFilter?: 'me' | 'all';
  'filter[status]'?: string | null;
  'filter[companyName]'?: string | null;
  'filter[owner]'?: string | null;
  'filter[subject]'?: string | null;
  'filter[priority]'?: string | null;
  'filter[customerResponded]'?: string | null;
}

export async function listProspects(
  params: ListProspectsParams = {}
): Promise<ProspectListResponse> {
  const query = new URLSearchParams();
  if (params.pageNumber) query.set('pageNumber', String(params.pageNumber));
  if (params.pageSize) query.set('pageSize', String(params.pageSize));
  if (params.sortBy) query.set('sortBy', params.sortBy);
  if (params.sortDir) query.set('sortDir', params.sortDir);
  if (params.ownerFilter) query.set('ownerFilter', params.ownerFilter);
  if (params['filter[status]']) query.set('filter[status]', params['filter[status]']!);
  if (params['filter[companyName]']) query.set('filter[companyName]', params['filter[companyName]']!);
  if (params['filter[owner]']) query.set('filter[owner]', params['filter[owner]']!);
  if (params['filter[subject]']) query.set('filter[subject]', params['filter[subject]']!);
  if (params['filter[priority]']) query.set('filter[priority]', params['filter[priority]']!);
  if (params['filter[customerResponded]']) query.set('filter[customerResponded]', params['filter[customerResponded]']!);
  const qs = query.toString();
  const response = await axiosClient.get<ProspectListResponse>(
    `/prospects${qs ? `?${qs}` : ''}`
  );
  return response.data;
}

export async function getProspect(id: string): Promise<Prospect> {
  const response = await axiosClient.get<Prospect>(`/prospects/${id}`);
  return response.data;
}

export async function getProspectFilterOptions(): Promise<ProspectFilterOptions> {
  const response = await axiosClient.get<ProspectFilterOptions>('/prospects/filter-options');
  return response.data;
}

export async function patchProspectStatus(
  id: string,
  status: number
): Promise<void> {
  await axiosClient.patch(`/prospects/${id}`, { status });
}

export async function promoteLeadToProspect(
  leadId: string,
  options: { file?: File; linkUrl?: string }
): Promise<{ prospectId: string; isNew: boolean }> {
  const formData = new FormData();
  if (options.file) formData.append('file', options.file);
  if (options.linkUrl) formData.append('linkUrl', options.linkUrl);
  const response = await axiosClient.post<{ prospectId: string; isNew: boolean }>(
    `/leads/${leadId}/promote`,
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } }
  );
  return response.data;
}

export async function replaceProspectPdf(id: string, file: File): Promise<void> {
  const formData = new FormData();
  formData.append('file', file);
  await axiosClient.post(`/prospects/${id}/pdf`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
}

export async function removeProspectPdf(id: string): Promise<void> {
  await axiosClient.delete(`/prospects/${id}/pdf`);
}

export async function assignProspect(id: string, ownerUserId: string | null): Promise<void> {
  await axiosClient.patch(`/prospects/${id}/assign`, { ownerId: ownerUserId });
}

export async function getProspectReplies(prospectId: string): Promise<ProspectReply[]> {
  const response = await axiosClient.get<ProspectReply[]>(`/prospects/${prospectId}/replies`);
  return response.data;
}

/** PATCH /prospects/{id}/mark-read — upserts ProspectReadState for the current user.
 *  Returns previousLastReadAt — the user's last-read timestamp before this call.
 *  Uses validateStatus so a 401/403 does NOT trigger the global redirect interceptor;
 *  callers must handle non-2xx by falling back gracefully. */
export const markProspectRead = async (id: string): Promise<{ previousLastReadAt: string | null }> => {
  const response = await axiosClient.patch<{ previousLastReadAt: string | null }>(
    `/prospects/${id}/mark-read`,
    undefined,
    { validateStatus: (s) => s < 500 },
  );
  if (response.status >= 200 && response.status < 300) {
    return response.data;
  }
  // Non-2xx (e.g. 401, 404): treat as never-read so the caller highlights everything.
  throw new Error(`mark-read returned ${response.status}`);
};
